import React, { useEffect, useState } from "react";
import ARAdvancedSection from "@/pages/finance/ARAdvancedSection";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { Plus, CheckCircle, Receipt, BookOpenText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { BANK_ACCOUNTS } from "./_shared";
import { GLAccountSelect, GLPreview, GLEntriesDialog, usePostingMap } from "./GLAccountBits";
import BranchFilter from "./BranchFilter";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

export default function ARTab({ canEdit, projects }) {
  const inspector = useRecordInspector();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [glView, setGlView] = useState(null);
  const [branch, setBranch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/finance/ar", { params: branch ? { branch_id: branch } : {} }),
        api.get("/finance/ar/summary"),
      ]);
      setRows(r.data);
      setSummary(s.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [branch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="dc-card p-4 border-l-4 border-l-[#1E5CBA]">
          <div className="overline">Total Outstanding</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-slate-900">{summary ? formatCurrency(summary.total_outstanding) : "—"}</div>
          <div className="text-xs text-slate-500">{summary?.count || 0} invoice(s)</div>
        </div>
        <div className="dc-card p-4 border-l-4 border-l-emerald-500">
          <div className="overline">Current 0–30d</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-emerald-700">{summary ? formatCurrency(summary.current) : "—"}</div>
        </div>
        <div className="dc-card p-4 border-l-4 border-l-amber-500">
          <div className="overline">Overdue 31–60d</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-amber-700">{summary ? formatCurrency(summary["31_60"]) : "—"}</div>
        </div>
        <div className="dc-card p-4 border-l-4 border-l-red-500">
          <div className="overline">Overdue 60d+</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-red-700">{summary ? formatCurrency(summary["60_plus"]) : "—"}</div>
        </div>
      </div>

      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Receivables</h3>
          <div className="flex items-center gap-2">
            <BranchFilter value={branch} onChange={setBranch} />
            {canEdit && (
              <Button onClick={() => setAddOpen(true)} className="btn-primary-blue" data-testid="ar-add-btn">
                <Plus size={14} className="mr-1" /> Add invoice
              </Button>
            )}
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Receipt} title="No receivables yet" description="Add an invoice to start tracking AR." />
        ) : (
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[1100px]">
              <thead>
                <tr>
                  <th>Client</th><th>Invoice</th><th>Date</th><th>Due</th>
                  <th className="text-right">Total</th><th className="text-right">Paid</th>
                  <th className="text-right">Balance</th><th>Days OD</th><th>Status</th>
                  <th className="w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="text-sm cursor-pointer hover:bg-slate-50/70 transition-colors"
                    onClick={() => inspector.open(r, {
                      title: `Invoice ${r.invoice_number}`,
                      subtitle: `${r.client_name} · ${r.project_name || ""} · ${formatCurrency(r.total)}`,
                    })}
                    data-testid={`ar-row-${r.invoice_number}`}
                  >
                    <td className="px-5 py-3 font-semibold text-slate-900">
                      {r.client_name}
                      <div className="text-xs text-slate-500">{r.project_name}</div>
                    </td>
                    <td className="px-5 py-3 font-mono-alt text-xs">{r.invoice_number}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs tabular-nums">{formatDate(r.invoice_date)}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs tabular-nums">{formatDate(r.due_date)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(r.total)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums text-slate-500">{formatCurrency(r.paid)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">{formatCurrency(r.balance)}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs text-red-600 tabular-nums">{r.days_overdue || "—"}</td>
                    <td className="px-5 py-3">
                      <StatusBadge status={
                        r.status === "current" ? "active"
                        : r.status === "due_soon" ? "expiring_soon"
                        : r.status === "overdue" ? "expired"
                        : r.status
                      } />
                    </td>
                    <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm" variant="outline" className="h-8"
                          title="View GL journal entries"
                          onClick={() => setGlView({ collection: "ar_invoices", id: r.id, label: r.invoice_number })}
                          data-testid={`ar-gl-${r.id}`}
                        >
                          <BookOpenText size={12} />
                        </Button>
                        {r.status !== "paid" && canEdit && (
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setPayOpen(r)} data-testid={`ar-pay-${r.id}`}>
                            <CheckCircle size={12} className="mr-1" />Receive
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ARAdvancedSection canEdit={canEdit} invoices={rows} onChanged={load} />

      <ARPaymentDialog item={payOpen} onClose={() => setPayOpen(null)} onDone={load} />
      <ARAddDialog open={addOpen} onClose={() => setAddOpen(false)} projects={projects} onDone={load} />
      <GLEntriesDialog item={glView} onClose={() => setGlView(null)} />
      <RecordInspector {...inspector.props} />
    </div>
  );
}

// Exported so APTab can reuse the same payment UX with a bill-shaped record.
// mode: "ar" (client receipt — bank debited) | "ap" (vendor payment — bank credited)
export function ARPaymentDialog({ item, onClose, onDone, endpoint, mode = "ar" }) {
  const isFc = item && item.currency && item.currency !== "USD";
  const pmap = usePostingMap();
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    amount: 0,
    fc_amount: 0,
    fx_rate: "",
    bank_account: BANK_ACCOUNTS[0],
    gl_account: "",
    reference: "",
    notes: "",
  });
  useEffect(() => {
    if (!item) return;
    if (item.currency && item.currency !== "USD") {
      const fcBalance = Math.max(0, (item.fc_total || 0) - (item.fc_paid || 0));
      setForm((f) => ({ ...f, fc_amount: fcBalance, amount: 0, fx_rate: "" }));
      api.get("/finance/fx/rates/resolve", { params: { currency: item.currency } })
        .then(({ data }) => setForm((f) => ({ ...f, fx_rate: data.rate })))
        .catch(() => {});
    } else {
      setForm((f) => ({ ...f, amount: item.balance }));
    }
  }, [item]);

  // Bank label → GL account code (mirrors the backend mapping); user can override.
  const bankGL = (label) => {
    const low = (label || "").toLowerCase();
    for (const [k, c] of Object.entries(pmap?.bank || {})) if (low.includes(k)) return c;
    return pmap?.defaults?.bank || "1120";
  };
  const effGl = form.gl_account || bankGL(form.bank_account);

  const submit = async () => {
    try {
      const body = { payment_date: form.payment_date, bank_account: form.bank_account, gl_account: effGl, reference: form.reference, notes: form.notes };
      if (isFc) {
        body.fc_amount = parseFloat(form.fc_amount || 0);
        if (form.fx_rate) body.fx_rate = parseFloat(form.fx_rate);
      } else {
        body.amount = parseFloat(form.amount || 0);
      }
      await api.post(endpoint || `/finance/ar/${item.id}/payment`, body);
      toast.success("Payment recorded");
      onClose();
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const usdEstimate = isFc && form.fx_rate > 0 ? (form.fc_amount || 0) / form.fx_rate : null;
  const previewAmt = isFc ? (usdEstimate || 0) : (form.amount || 0);
  const glRows = previewAmt > 0
    ? (mode === "ap"
        ? [{ code: "2110", debit: previewAmt }, { code: effGl, credit: previewAmt }]
        : [{ code: effGl, debit: previewAmt }, { code: "1130", credit: previewAmt }])
    : [];

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        {item && (
          <>
            <DialogHeader>
              <DialogTitle>Record payment · {item.invoice_number}</DialogTitle>
              <DialogDescription>
                {item.client_name} · Balance {formatCurrency(item.balance)}
                {isFc && ` · ${item.currency} booked @ ${item.fx_rate}/USD`}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Payment date</Label><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} /></div>
              {isFc ? (
                <>
                  <div><Label>Amount ({item.currency})</Label><Input type="number" step="0.01" value={form.fc_amount} onChange={(e) => setForm({ ...form, fc_amount: parseFloat(e.target.value || 0) })} data-testid="pay-fc-amount" /></div>
                  <div>
                    <Label>Settlement rate ({item.currency}/USD)</Label>
                    <Input type="number" step="0.000001" value={form.fx_rate} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} data-testid="pay-fx-rate" />
                    {usdEstimate != null && <div className="mt-1 text-[10px] text-slate-500">≈ {formatCurrency(usdEstimate)} USD · realized FX gain/loss posts automatically</div>}
                  </div>
                </>
              ) : (
                <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || 0) })} /></div>
              )}
              <div>
                <Label>{mode === "ap" ? "Bank paid from" : "Bank received into"}</Label>
                <Select value={form.bank_account} onValueChange={(v) => setForm({ ...form, bank_account: v, gl_account: "" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{BANK_ACCOUNTS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>GL account {mode === "ap" ? "(credited — money out)" : "(debited — money in)"}</Label>
                <GLAccountSelect value={effGl} onChange={(v) => setForm({ ...form, gl_account: v })} types={["asset"]} testid="pay-gl-account" />
              </div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="col-span-2">
                <GLPreview
                  rows={glRows}
                  note={isFc
                    ? "Posted in USD at the settlement rate — any realized FX gain/loss posts to 6920 automatically."
                    : mode === "ap"
                      ? "Settles the vendor payable (2110) and credits the selected bank/cash account."
                      : "Debits the selected bank/cash account and settles the receivable (1130)."}
                  testid="pay-gl-preview"
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} className="btn-primary-blue" data-testid="ar-pay-confirm">Save payment</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ARAddDialog({ open, onClose, projects, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const due30 = new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10);
  const blank = {
    client_name: "", project_id: "", project_name: "",
    invoice_date: today, due_date: due30, total: 0, notes: "", revenue_treatment: "earned",
    currency: "USD", fx_rate: "", tax_code: "none", revenue_account: "",
  };
  const [form, setForm] = useState(blank);
  const [taxCodes, setTaxCodes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [dupWarn, setDupWarn] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  useEffect(() => {
    if (open) {
      setForm(blank);
      setDupWarn(null);
      setLineItems([]);
      api.get("/finance/tax/codes").then(({ data }) => setTaxCodes(data.items || [])).catch(() => {});
      api.get("/finance/fx/currencies").then(({ data }) => setCurrencies(data.items || [])).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setCurrency = async (cur) => {
    setForm((f) => ({ ...f, currency: cur, fx_rate: "" }));
    if (cur !== "USD") {
      try {
        const { data } = await api.get("/finance/fx/rates/resolve", { params: { currency: cur } });
        setForm((f) => ({ ...f, currency: cur, fx_rate: data.rate }));
      } catch { /* rate stays blank — backend will reject with a clear error */ }
    }
  };

  const taxRate = form.tax_code !== "none" ? (taxCodes.find((t) => t.code === form.tax_code)?.rate_pct || 0) : 0;
  const liTotal = lineItems.reduce((s, li) => s + (li.qty || 1) * (li.unit_price || 0), 0);
  const effTotal = lineItems.length > 0 ? Math.round(liTotal * 100) / 100 : (form.total || 0);
  const taxAmt = effTotal * taxRate / 100;
  const isFc = form.currency !== "USD";
  const curOptions = [...new Set(["EUR", "GBP", "KES", "ETB", "SOS", "AED", ...currencies.map((c) => c.currency)])];
  const effRev = form.revenue_account || "4110";
  const glRows = effTotal > 0
    ? [
        { code: "1130", debit: effTotal + taxAmt },
        { code: form.revenue_treatment === "deferred" ? "2150" : effRev, credit: effTotal },
        ...(taxAmt > 0 ? [{ code: "2131", credit: taxAmt }] : []),
      ]
    : [];

  const pullDeliverables = () => {
    const proj = projects.find((p) => p.id === form.project_id);
    if (!proj) return toast.error("Select a project first");
    const done = (proj.deliverables || []).filter(
      (d) => d.status === "completed" || (d.completion_pct || 0) >= 100);
    if (!done.length) return toast.error("No completed deliverables on this project yet");
    setLineItems([
      ...lineItems,
      ...done.map((d) => ({ description: d.title || d.description || "Deliverable", qty: 1, unit_price: 0 })),
    ]);
    if (!form.client_name && proj.client) setForm((f) => ({ ...f, client_name: proj.client }));
    toast.success(`${done.length} completed deliverable(s) added as line items — set unit prices`);
  };

  const submit = async (force = false) => {
    if (!form.client_name.trim()) return toast.error("Client name is required");
    if (effTotal <= 0) return toast.error("Invoice subtotal must be greater than 0");
    try {
      const proj = projects.find((p) => p.id === form.project_id);
      const body = {
        client_name: form.client_name, project_id: form.project_id,
        project_name: proj?.name || form.project_name,
        invoice_date: form.invoice_date, due_date: form.due_date,
        notes: form.notes, revenue_treatment: form.revenue_treatment,
        revenue_account: form.revenue_treatment === "earned" ? effRev : "",
        currency: form.currency,
        tax_code: form.tax_code === "none" ? "" : form.tax_code,
        line_items: lineItems.map((li) => ({
          ...li, amount: Math.round((li.qty || 1) * (li.unit_price || 0) * 100) / 100,
        })),
        source_type: form.project_id ? "project" : "",
        source_id: form.project_id || "",
        force,
      };
      if (isFc) {
        body.fc_total = effTotal;
        body.total = 0;
        if (form.fx_rate) body.fx_rate = parseFloat(form.fx_rate);
      } else {
        body.total = effTotal;
      }
      await api.post("/finance/ar", body);
      setDupWarn(null);
      toast.success(force ? "Invoice created (duplicate override recorded)" : "Invoice created");
      onClose();
      onDone();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === "duplicate_suspected") {
        setDupWarn(det);
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Client *</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="ar-add-client" /></div>
          <div className="col-span-2">
            <Label>Project</Label>
            <div className="flex gap-2">
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              {form.project_id && (
                <Button type="button" variant="outline" className="h-10 shrink-0 text-xs" onClick={pullDeliverables} data-testid="ar-pull-deliverables">
                  Pull deliverables
                </Button>
              )}
            </div>
          </div>
          {/* Line items — unified invoice builder */}
          <div className="col-span-2">
            <div className="flex items-center justify-between">
              <Label>Line items</Label>
              <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-[10px]"
                onClick={() => setLineItems([...lineItems, { description: "", qty: 1, unit_price: 0 }])}
                data-testid="ar-line-add">+ Add line</Button>
            </div>
            {lineItems.map((li, i) => (
              <div key={i} className="mt-1.5 grid grid-cols-12 gap-1.5">
                <Input className="col-span-6 h-8" placeholder="Description" value={li.description}
                  onChange={(e) => setLineItems(lineItems.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                  data-testid={`ar-line-desc-${i}`} />
                <Input className="col-span-2 h-8" type="number" step="0.01" placeholder="Qty" value={li.qty}
                  onChange={(e) => setLineItems(lineItems.map((x, j) => j === i ? { ...x, qty: parseFloat(e.target.value || 0) } : x))}
                  data-testid={`ar-line-qty-${i}`} />
                <Input className="col-span-2 h-8" type="number" step="0.01" placeholder="Unit price" value={li.unit_price}
                  onChange={(e) => setLineItems(lineItems.map((x, j) => j === i ? { ...x, unit_price: parseFloat(e.target.value || 0) } : x))}
                  data-testid={`ar-line-price-${i}`} />
                <div className="col-span-1 flex h-8 items-center justify-end font-mono-alt text-[11px] tabular-nums text-slate-600">
                  {(((li.qty || 1) * (li.unit_price || 0)) || 0).toFixed(2)}
                </div>
                <Button type="button" variant="outline" className="col-span-1 h-8 px-0"
                  onClick={() => setLineItems(lineItems.filter((_, j) => j !== i))}
                  data-testid={`ar-line-remove-${i}`}>×</Button>
              </div>
            ))}
            {lineItems.length > 0 && (
              <div className="mt-1 text-right text-[11px] text-slate-500" data-testid="ar-line-subtotal">
                Line-item subtotal: <b className="font-mono-alt tabular-nums">{effTotal.toFixed(2)} {form.currency}</b>
              </div>
            )}
          </div>
          <div><Label>Invoice date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
          <div><Label>Due date *</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          <div>
            <Label>Currency</Label>
            <Select value={form.currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="ar-add-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD (base)</SelectItem>
                {curOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isFc && (
            <div>
              <Label>FX rate ({form.currency}/USD)</Label>
              <Input type="number" step="0.000001" value={form.fx_rate} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} data-testid="ar-add-fx-rate" />
            </div>
          )}
          <div className={isFc ? "col-span-2" : ""}>
            <Label>Subtotal ({form.currency}) *</Label>
            <Input type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: parseFloat(e.target.value || 0) })} data-testid="ar-add-total" />
          </div>
          <div className={isFc ? "col-span-2" : ""}>
            <Label>Tax</Label>
            <Select value={form.tax_code} onValueChange={(v) => setForm({ ...form, tax_code: v })}>
              <SelectTrigger data-testid="ar-add-tax-code"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No tax</SelectItem>
                {taxCodes.filter((t) => t.kind === "output").map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {taxRate > 0 && (
              <div className="mt-1 text-[10px] text-slate-500" data-testid="ar-add-tax-hint">
                + {taxRate}% tax {taxAmt.toFixed(2)} {form.currency} → gross {(effTotal + taxAmt).toFixed(2)} {form.currency}
              </div>
            )}
          </div>
          <div className="col-span-2">
            <Label>Revenue treatment</Label>
            <Select value={form.revenue_treatment} onValueChange={(v) => setForm({ ...form, revenue_treatment: v })}>
              <SelectTrigger data-testid="ar-add-revenue-treatment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="earned">Earned now (revenue on invoice)</SelectItem>
                <SelectItem value="deferred">Deferred (recognize on schedule)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.revenue_treatment === "earned" && (
            <div className="col-span-2">
              <Label>Revenue account (credited)</Label>
              <GLAccountSelect value={effRev} onChange={(v) => setForm({ ...form, revenue_account: v })} types={["revenue"]} testid="ar-add-revenue-account" />
              <div className="mt-1 text-[10px] text-slate-500">The receivable (1130) is debited automatically — you only pick where the revenue lands.</div>
            </div>
          )}
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="col-span-2">
            <GLPreview
              rows={glRows}
              currency={form.currency}
              note={isFc ? "Posted to the GL in USD at the booking FX rate." : undefined}
              testid="ar-add-gl-preview"
            />
          </div>
        </div>
        {dupWarn && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="ar-dup-warning">
            <div className="text-xs font-semibold text-amber-800">⚠ Possible duplicate invoice</div>
            <div className="mt-1 text-xs text-amber-900">{dupWarn.message}</div>
            <ul className="mt-1.5 space-y-0.5">
              {(dupWarn.matches || []).map((m) => (
                <li key={m.id} className="text-[11px] font-mono-alt text-amber-800">
                  {m.invoice_number} · {m.client_name} · {formatCurrency(m.total)} · {formatDate(m.invoice_date)} · {m.status}
                </li>
              ))}
            </ul>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" className="h-7 border-amber-400 text-amber-800 hover:bg-amber-100"
                onClick={() => submit(true)} data-testid="ar-dup-force">
                Create anyway (override is audit-logged)
              </Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => setDupWarn(null)} data-testid="ar-dup-dismiss">
                Dismiss
              </Button>
            </div>
          </div>
        )}
        <DialogFooter><Button onClick={() => submit(false)} className="btn-primary-blue" data-testid="ar-add-confirm">Create invoice</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
